import { type ComponentType, lazy, type ReactNode, Suspense } from 'react'
import { Navigate, Route, useParams, useSearchParams } from 'react-router'
import { useSession } from '@/lib/auth/auth-client'
import { loadIntegrationCatalogue } from '@/lib/ondit/integration-catalogue'
import ChatError from '@/screens/workspace/chat/conversation/error'
import type { ErrorBoundaryProps } from '@/screens/workspace/components'
import HomeError from '@/screens/workspace/home/error'
import DocumentLoading from '@/screens/workspace/knowledge/base/document/loading'
import KnowledgeBaseError from '@/screens/workspace/knowledge/base/error'
import KnowledgeBaseLoading from '@/screens/workspace/knowledge/base/loading'
import KnowledgeError from '@/screens/workspace/knowledge/error'
import KnowledgeLoading from '@/screens/workspace/knowledge/loading'
import WorkspaceNotFound from '@/screens/workspace/not-found'
import SearchError from '@/screens/workspace/search/error'
import SettingsError from '@/screens/workspace/settings/error'
import TablesError from '@/screens/workspace/tables/error'
import TablesLoading from '@/screens/workspace/tables/loading'
import TableError from '@/screens/workspace/tables/table/error'
import TableLoading from '@/screens/workspace/tables/table/loading'
import WorkflowLayout from '@/screens/workspace/w/workflow/layout'
import { RouteBoundary } from '~/core/ui/boundary'
import { ComposerSkeleton, ListSkeleton, TableSkeleton } from '~/core/ui/skeletons'

const Home = lazy(() => import('@/screens/workspace/home/home').then((m) => ({ default: m.Home })))
const Archived = lazy(() => import('@/client/archived'))
const Workflow = lazy(() => withCatalogue(import('@/screens/workspace/w/workflow/workflow')))
const Tables = lazy(() =>
  import('@/screens/workspace/tables/tables').then((m) => ({ default: m.Tables }))
)
const Table = lazy(() =>
  import('@/screens/workspace/tables/table/table').then((m) => ({ default: m.Table }))
)
const Knowledge = lazy(() =>
  import('@/screens/workspace/knowledge/knowledge').then((m) => ({
    default: m.Knowledge,
  }))
)
/**
 * The workflow editor offers every provider block the runtime runs, so it
 * fetches the catalogue with its own chunk.
 */
const withCatalogue = <T,>(page: Promise<T>): Promise<T> =>
  Promise.all([page, loadIntegrationCatalogue()]).then(([m]) => m)
const Search = lazy(() =>
  import('@/screens/workspace/search/search').then((m) => ({ default: m.Search }))
)
const KnowledgeBase = lazy(() =>
  import('@/screens/workspace/knowledge/base/base').then((m) => ({
    default: m.KnowledgeBase,
  }))
)
const Document = lazy(() =>
  import('@/screens/workspace/knowledge/base/document/document').then((m) => ({
    default: m.Document,
  }))
)

/**
 * One surface: the boundary that catches its throws and the skeleton that
 * stands in while its chunk arrives.
 *
 * Both are what the segment already had under Next — `error.tsx` and
 * `loading.tsx` beside the page — and the router mounts neither on its own. The
 * boundary is outside the Suspense so that a chunk which fails to load is
 * caught too. A surface that shipped no skeleton gets the shared one closest to
 * its shape rather than the brand seal, which is for a page with no frame yet.
 */
export function Surface({
  boundary,
  skeleton,
  children,
}: {
  boundary: ComponentType<ErrorBoundaryProps>
  skeleton: ReactNode
  children: ReactNode
}) {
  return (
    <RouteBoundary fallback={boundary}>
      <Suspense fallback={skeleton}>{children}</Suspense>
    </RouteBoundary>
  )
}

function Detail({ kind }: { kind: 'knowledge' | 'document' }) {
  const { id = '', documentId = '' } = useParams()
  const [query] = useSearchParams()
  switch (kind) {
    case 'knowledge':
      return <KnowledgeBase id={id} knowledgeBaseName={query.get('kbName') || 'Knowledge Base'} />
    case 'document':
      return (
        <Document
          knowledgeBaseId={id}
          documentId={documentId}
          knowledgeBaseName={query.get('kbName') || 'Knowledge Base'}
          documentName={query.get('docName') || 'Document'}
        />
      )
  }
}

function Chat() {
  const { chatId } = useParams()
  const { data } = useSession()
  return <Home chatId={chatId} userId={data?.user?.id} userName={data?.user?.name ?? undefined} />
}

/**
 * The Agent module's screens, as one route table.
 *
 * It lives here rather than beside the application router because it is the
 * Agent's: every entry in it names a file in this tree, and the boundaries and
 * skeletons it mounts are the ones each surface ships next to itself. The
 * router in `app/src` supplies the path it hangs under and nothing else.
 *
 * Returned as a fragment of `<Route>` children rather than a component —
 * `<Routes>` reads its children as a table, so a component here would be
 * rendered as an element and contribute no routes at all.
 *
 * Help is not here: it is the one screen in this tree reached from outside a
 * workspace, so `app/src/modules/agent` mounts it above the module at `/help`.
 */
export function agentRouteChildren() {
  return (
    <>
      <Route index element={<Navigate to='home' replace />} />
      <Route
        path='home'
        element={
          <Surface boundary={HomeError} skeleton={<ComposerSkeleton />}>
            <Chat />
          </Surface>
        }
      />
      <Route
        path='chat/:chatId'
        element={
          <Surface boundary={ChatError} skeleton={<ComposerSkeleton />}>
            <Chat />
          </Surface>
        }
      />
      <Route
        path='w/:workflowId'
        element={
          <WorkflowLayout>
            <Workflow />
          </WorkflowLayout>
        }
      />
      <Route path='w' element={<Navigate to='../home' replace />} />
      <Route
        path='tables'
        element={
          <Surface boundary={TablesError} skeleton={<TablesLoading />}>
            <Tables />
          </Surface>
        }
      />
      <Route
        path='tables/:tableId'
        element={
          <Surface boundary={TableError} skeleton={<TableLoading />}>
            <Table />
          </Surface>
        }
      />
      <Route
        path='knowledge/:id'
        element={
          <Surface boundary={KnowledgeBaseError} skeleton={<KnowledgeBaseLoading />}>
            <Detail kind='knowledge' />
          </Surface>
        }
      />
      <Route
        path='knowledge/:id/:documentId'
        element={
          <Surface boundary={KnowledgeBaseError} skeleton={<DocumentLoading />}>
            <Detail kind='document' />
          </Surface>
        }
      />
      <Route
        path='knowledge'
        element={
          <Surface boundary={KnowledgeError} skeleton={<KnowledgeLoading />}>
            <Knowledge />
          </Surface>
        }
      />
      <Route
        path='search'
        element={
          <Surface
            boundary={SearchError}
            skeleton={<ListSkeleton className='mx-auto max-w-(--prose-width) p-(--s-10)' />}
          >
            <Search />
          </Surface>
        }
      />
      <Route
        path='settings/recently-deleted'
        element={
          <Surface boundary={SettingsError} skeleton={<TableSkeleton className='p-(--s-10)' />}>
            <Archived />
          </Surface>
        }
      />
      <Route path='*' element={<WorkspaceNotFound />} />
    </>
  )
}
